import { Injectable } from '@angular/core';
import * as CryptoJS from 'crypto-js';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {

  constructor() {}

  /**
   * Encrypt and store data in local storage
   * @param key - Storage key
   * @param value - Value to store
   */
  async set(key: string, value: any): Promise<void> {
    try {
      const encrypted = CryptoJS.AES.encrypt(
        JSON.stringify(value),
        environment.encryptionKey
      ).toString();
      localStorage.setItem(key, encrypted);
    } catch (error) {
      console.error(`❌ Error storing ${key}:`, error);
      throw error;
    }
  }

  /**
   * Retrieve and decrypt data from local storage
   * @param key - Storage key
   * @returns Decrypted value or null if not found
   */
  async get(key: string): Promise<any> {
    try {
      const encrypted = localStorage.getItem(key);

      if (encrypted == null) {
        return null;
      }

      try {
        const bytes = CryptoJS.AES.decrypt(encrypted, environment.encryptionKey);
        const decrypted = bytes.toString(CryptoJS.enc.Utf8);

        if (!decrypted) {
          console.error(`❌ Failed to decrypt data for key '${key}'`);
          return null;
        }

        return JSON.parse(decrypted);
      } catch (decryptError) {
        console.error(`❌ Error decrypting data for key '${key}':`, decryptError);
        return null;
      }
    } catch (error) {
      console.error(`❌ Error retrieving ${key}:`, error);
      return null;
    }
  }

  /**
   * Remove item by key from local storage
   * @param key - Storage key to remove
   */
  async remove(key: string): Promise<void> {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`❌ Error removing ${key}:`, error);
      throw error;
    }
  }

  /**
   * Clear specific authentication keys
   */
  async clearAll(): Promise<void> {
    try {
      localStorage.removeItem('ec-user');
      localStorage.removeItem('ec-token');
      localStorage.removeItem('auth-token');
      localStorage.removeItem('refresh-token');
    } catch (error) {
      console.error('❌ Error clearing authentication data:', error);
      throw error;
    }
  }

  /**
   * Clear ALL local storage data (use with caution)
   */
  async clearEverything(): Promise<void> {
    try {
      localStorage.clear();
    } catch (error) {
      console.error('❌ Error clearing all storage:', error);
      throw error;
    }
  }

  /**
   * Check if a key exists in storage
   * @param key - Storage key to check
   * @returns true if key exists, false otherwise
   */
  async has(key: string): Promise<boolean> {
    try {
      return localStorage.getItem(key) !== null;
    } catch (error) {
      console.error(`❌ Error checking if key ${key} exists:`, error);
      return false;
    }
  }

  /**
   * Get all keys stored in local storage
   * @returns Array of all storage keys
   */
  async getAllKeys(): Promise<string[]> {
    try {
      return Object.keys(localStorage);
    } catch (error) {
      console.error('❌ Error getting all keys:', error);
      return [];
    }
  }

  /**
   * Store data without encryption (for non-sensitive data)
   * @param key - Storage key
   * @param value - Value to store
   */
  async setUnencrypted(key: string, value: any): Promise<void> {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(`❌ Error storing ${key} (unencrypted):`, error);
      throw error;
    }
  }

  /**
   * Retrieve data without decryption (for non-sensitive data)
   * @param key - Storage key
   * @returns Parsed value or null if not found
   */
  async getUnencrypted(key: string): Promise<any> {
    try {
      const value = localStorage.getItem(key);

      if (value == null) {
        return null;
      }

      try {
        return JSON.parse(value);
      } catch (parseError) {
        console.error(`❌ Error parsing data for key '${key}':`, parseError);
        return value;
      }
    } catch (error) {
      console.error(`❌ Error retrieving ${key} (unencrypted):`, error);
      return null;
    }
  }

  /**
   * Get the storage type description
   */
  getStorageType(): string {
    return 'Local Storage (encrypted)';
  }

  // ── Card token helpers ──────────────────────────────────────────

  private readonly CARD_TOKEN_PREFIX = 'card_token_';

  /** Store a card authorization token (encrypted) */
  async storeCardToken(cardId: string, authorizationCode: string): Promise<void> {
    await this.set(`${this.CARD_TOKEN_PREFIX}${cardId}`, authorizationCode);
  }

  /** Retrieve a card authorization token */
  async getCardToken(cardId: string): Promise<string | null> {
    const token = await this.get(`${this.CARD_TOKEN_PREFIX}${cardId}`);
    return token ?? null;
  }

  /** Remove a card authorization token */
  async removeCardToken(cardId: string): Promise<void> {
    await this.remove(`${this.CARD_TOKEN_PREFIX}${cardId}`);
  }

  /** Store multiple card tokens at once */
  async storeCardTokens(tokens: Map<string, string>): Promise<void> {
    await Promise.all(
      Array.from(tokens.entries()).map(([cardId, code]) =>
        this.storeCardToken(cardId, code)
      )
    );
  }

  /** Clear all stored card tokens */
  async clearAllCardTokens(): Promise<void> {
    const keys = await this.getAllKeys();
    const cardKeys = keys.filter(key => key.startsWith(this.CARD_TOKEN_PREFIX));
    await Promise.all(cardKeys.map(key => this.remove(key)));
  }

  /** Get count of stored card tokens */
  async getStoredTokenCount(): Promise<number> {
    const keys = await this.getAllKeys();
    return keys.filter(key => key.startsWith(this.CARD_TOKEN_PREFIX)).length;
  }
}
